import { useState } from 'react'
import { Button, InlineAlert } from '@design-systems/canvas-ui'
import Icon, {
  CheckmarkIcon,
  PencilIcon,
  WarningIcon,
} from '@design-systems/react-icons'
import DraftGroupCard from './DraftGroupCard'
import {
  isSectionApproved,
  isSectionConflicted,
  isSectionLocked,
} from '../config/draftReviewConfig'
import { getSectionValue, spaceLineBreaks } from '../helpers/draftReviewHelpers'

// The review screen itself. Sections, their titles and their grouping all
// come from the API — this component only decides how they are laid out.
const DraftReviewView = ({
  error = '',
  // Set once the review is over, which turns the screen into a record of
  // what was decided. Comes from brand_lifecycle_status, not from the review.
  isReadOnly = false,
  isSaving = false,
  onApproveGroup,
  onEditApprovedGroup,
  onEditGroup,
  onResolveSection,
  onUndoGroup,
  review,
}) => {
  const { approvedCount, groups, totalSectionCount } = review
  // `null` while not editing, otherwise the edited text keyed by section id,
  // for every group at once.
  const [bulkDrafts, setBulkDrafts] = useState(null)
  const allSections = groups.flatMap(({ sections }) => sections)
  // Once everything is approved one Edit opens every group together.
  const canEditAll =
    !isReadOnly &&
    allSections.length > 0 &&
    allSections.every(
      ({ status }) => isSectionApproved(status) && !isSectionLocked(status),
    )
  const isBulkEditing = canEditAll && bulkDrafts !== null

  const startBulkEdit = () =>
    setBulkDrafts(
      Object.fromEntries(
        allSections.map((section) => [
          section.id,
          spaceLineBreaks(getSectionValue(section)),
        ]),
      ),
    )
  // Only sections whose text differs from what the edit box opened with.
  const changedDrafts = bulkDrafts
    ? Object.fromEntries(
        allSections
          .filter(
            (section) =>
              bulkDrafts[section.id] !==
              spaceLineBreaks(getSectionValue(section)),
          )
          .map(({ id }) => [id, bulkDrafts[id]]),
      )
    : {}
  const saveBulkEdit = async () => {
    if (await onEditApprovedGroup(changedDrafts)) setBulkDrafts(null)
  }
  // The documents the sections on screen were extracted from.
  const documentCount = new Set(
    groups
      .flatMap(({ sections }) => sections)
      .flatMap(({ candidates }) =>
        candidates.flatMap(({ sourceFiles }) =>
          sourceFiles.map(({ name }) => name),
        ),
      ),
  ).size
  // A picked option takes the section out of conflict, so this counts only
  // the decisions still waiting.
  const conflictCount = groups
    .flatMap(({ sections }) => sections)
    .filter(({ status }) => isSectionConflicted(status)).length

  return (
    <section aria-label="Review extracted sections" className="draft-review">
      <div className="draft-review__toolbar">
        <span className="draft-review__summary">
          {documentCount} documents · {approvedCount} of {totalSectionCount}{' '}
          sections approved
        </span>
        {!isReadOnly && conflictCount > 0 && (
          <span className="draft-review__conflicts" role="status">
            <Icon size="sm" svg={WarningIcon} />
            {conflictCount} section(s) have conflicting values — pick one in
            each to continue
          </span>
        )}
        {canEditAll &&
          (isBulkEditing ? (
            <span className="draft-section__actions draft-review__edit">
              <Button
                isDisabled={isSaving}
                onClick={() => setBulkDrafts(null)}
                variant="secondary"
              >
                Cancel
              </Button>
              <Button
                isDisabled={
                  isSaving ||
                  Object.keys(changedDrafts).length === 0 ||
                  Object.values(changedDrafts).some((value) => !value.trim())
                }
                onClick={saveBulkEdit}
                variant="primary"
              >
                <span className="upload-documents__button-content">
                  <Icon size="sm" svg={CheckmarkIcon} /> Save
                </span>
              </Button>
            </span>
          ) : (
            <Button
              className="draft-review__edit"
              isDisabled={isSaving}
              onClick={startBulkEdit}
              variant="secondary"
            >
              <span className="upload-documents__button-content">
                <Icon size="sm" svg={PencilIcon} /> Edit
              </span>
            </Button>
          ))}
        {!isReadOnly && !canEditAll && (
          <span className="draft-review__hint">
            Every section must have content before it can be approved.
          </span>
        )}
      </div>

      {error && (
        <InlineAlert heading="Unable to save that change" variant="error">
          {error}
        </InlineAlert>
      )}

      {groups.length === 0 ? (
        <p className="draft-review__empty">
          No extracted sections have come back for this brand yet.
        </p>
      ) : (
        groups.map((group) => (
          <DraftGroupCard
            bulkDrafts={isBulkEditing ? bulkDrafts : null}
            group={group}
            isReadOnly={isReadOnly}
            isSaving={isSaving}
            key={group.id}
            onApprove={onApproveGroup}
            onBulkChange={(id, value) =>
              setBulkDrafts((current) => ({ ...current, [id]: value }))
            }
            onEdit={onEditGroup}
            onResolve={onResolveSection}
            onUndo={onUndoGroup}
          />
        ))
      )}

      <p className="draft-review__footnote">
        {approvedCount} of {totalSectionCount} sections approved
      </p>
    </section>
  )
}

export default DraftReviewView
